import React, { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Tooltip,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import api from "../../axiosInstance";
import { toast } from "react-toastify";

const ACCENT = "#17479e";
const HEADER_GRADIENT = `linear-gradient(135deg, ${ACCENT} 0%, #1565c0 100%)`;

const formatAmount = (value) =>
  value === null || value === undefined ? "—" : Number(value).toString();

const formatRange = (min, max) => {
  if (min == null && max == null) return "—";
  return `${formatAmount(min)} – ${formatAmount(max)}`;
};

// Same bounds the backend enforces; checked here only to flag the field before saving.
const rangeError = (row, value) => {
  if (value === "") return "Required";
  const n = Number(value);
  if (Number.isNaN(n) || n < 0) return "Must be 0 or more";
  if (row.minPrice != null && n < Number(row.minPrice))
    return `Min ${formatAmount(row.minPrice)}`;
  if (row.maxPrice != null && n > Number(row.maxPrice))
    return `Max ${formatAmount(row.maxPrice)}`;
  return "";
};

const isChanged = (row, value) =>
  value !== "" && (row.price == null || Number(value) !== Number(row.price));

function MentorSessionPriceDialog({ open, mentorRow, onClose, onSuccess }) {
  const mentorProfileId = mentorRow?.mentorId;
  const [rows, setRows] = useState([]);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resettingId, setResettingId] = useState(null);

  const applyRows = (data) => {
    const list = Array.isArray(data) ? data : [];
    setRows(list);
    setValues(
      Object.fromEntries(
        list.map((r) => [r.sessionTypeId, r.price == null ? "" : String(r.price)]),
      ),
    );
  };

  const loadPrices = useCallback(() => {
    if (!mentorProfileId) return;
    setLoading(true);
    api
      .get(`/admin/mentors/${mentorProfileId}/session-prices`)
      .then((res) => applyRows(res.data?.data))
      .catch((err) => {
        console.error("Mentor session prices fetch error:", err);
        toast.error("Failed to load session prices.");
      })
      .finally(() => setLoading(false));
  }, [mentorProfileId]);

  useEffect(() => {
    if (!open) return;
    setRows([]);
    setValues({});
    loadPrices();
  }, [open, loadPrices]);

  const changedRows = rows.filter((r) =>
    isChanged(r, values[r.sessionTypeId] ?? ""),
  );
  const hasErrors = changedRows.some((r) =>
    rangeError(r, values[r.sessionTypeId] ?? ""),
  );

  const handleSave = async () => {
    if (!changedRows.length || hasErrors) return;
    setSaving(true);
    try {
      const res = await api.put(
        `/admin/mentors/${mentorProfileId}/session-prices`,
        {
          prices: changedRows.map((r) => ({
            sessionTypeId: r.sessionTypeId,
            price: Number(values[r.sessionTypeId]),
          })),
        },
      );
      applyRows(res.data?.data);
      toast.success("Session prices updated successfully!");
      onSuccess?.();
    } catch (err) {
      toast.error(
        err.response?.data?.errorMessage ||
          "Failed to update session prices. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async (row) => {
    setResettingId(row.sessionTypeId);
    try {
      const res = await api.delete(
        `/admin/mentors/${mentorProfileId}/session-prices/${row.sessionTypeId}`,
      );
      applyRows(res.data?.data);
      toast.success(`${row.sessionTypeName} reset to platform default.`);
      onSuccess?.();
    } catch (err) {
      toast.error(
        err.response?.data?.errorMessage ||
          "Failed to reset session price. Please try again.",
      );
    } finally {
      setResettingId(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden" } }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2,
          background: HEADER_GRADIENT,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: "10px",
              background: "rgba(255,255,255,0.15)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CurrencyRupeeIcon sx={{ color: "#fff", fontSize: 18 }} />
          </Box>
          <Box>
            <Typography
              sx={{ color: "#fff", fontWeight: 700, fontSize: "1.05rem" }}
            >
              Manage Session Prices
            </Typography>
            <Typography
              sx={{ color: "rgba(255,255,255,0.8)", fontSize: "0.75rem" }}
            >
              {mentorRow?.mentorName ?? "—"}
            </Typography>
          </Box>
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="close">
          <CloseIcon sx={{ color: "#fff", fontSize: 20 }} />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          These prices also apply to this mentor's Razorpay package prices.
          Apple in-app purchase prices are not affected. The mentor can still
          change their own prices later.
        </Alert>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : rows.length === 0 ? (
          <Typography sx={{ color: "#757575", textAlign: "center", py: 3 }}>
            No active session types.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Session Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Platform Default</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Allowed Range</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Mentor Price</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => {
                const value = values[r.sessionTypeId] ?? "";
                const error = isChanged(r, value) ? rangeError(r, value) : "";
                return (
                  <TableRow key={r.sessionTypeId}>
                    <TableCell>{r.sessionTypeName}</TableCell>
                    <TableCell>{formatAmount(r.platformDefaultPrice)}</TableCell>
                    <TableCell>{formatRange(r.minPrice, r.maxPrice)}</TableCell>
                    <TableCell sx={{ minWidth: 140 }}>
                      <TextField
                        size="small"
                        type="number"
                        value={value}
                        error={Boolean(error)}
                        helperText={error || " "}
                        inputProps={{
                          min: 0,
                          "aria-label": `${r.sessionTypeName} price`,
                        }}
                        onChange={(e) =>
                          setValues((prev) => ({
                            ...prev,
                            [r.sessionTypeId]: e.target.value,
                          }))
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={r.isCustom ? "Custom" : "Default"}
                        color={r.isCustom ? "primary" : "default"}
                        variant={r.isCustom ? "filled" : "outlined"}
                      />
                    </TableCell>
                    <TableCell>
                      {r.isCustom && (
                        <Tooltip title="Reset to platform default" arrow>
                          <span>
                            <IconButton
                              size="small"
                              color="warning"
                              aria-label={`Reset ${r.sessionTypeName}`}
                              disabled={resettingId !== null || saving}
                              onClick={() => handleReset(r)}
                            >
                              {resettingId === r.sessionTypeId ? (
                                <CircularProgress size={16} />
                              ) : (
                                <RestartAltIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 0.5 }}>
        <Button
          onClick={onClose}
          sx={{
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: 600,
            color: "#616161",
          }}
        >
          Close
        </Button>
        <Button
          variant="contained"
          disabled={saving || loading || !changedRows.length || hasErrors}
          onClick={handleSave}
          sx={{
            borderRadius: "10px",
            textTransform: "none",
            color: "#fff !important",
            fontWeight: 700,
            px: 3,
            background: HEADER_GRADIENT,
            boxShadow: "none",
          }}
        >
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default MentorSessionPriceDialog;
